import React from 'react';
import { useTrackWhen } from '../lib/analytics';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { getComparison } from '../compare/registry';
import { BI_QUIZ, BOOKING_URL, ECOMMERCE_QUIZ, answersFromSearch, score, searchWithAnswers } from './toolQuizzes';
import type { Answers, Quiz } from './toolQuizzes';

const SITE_URL = 'https://www.greyfielddata.com';

interface Variant {
  quiz: Quiz;
  name: string;
  seoTitle: string;
  description: string;
  eyebrow: string;
  heading: string;
  intro: string;
  extraLinks: { href: string; label: string }[];
  scoringNote: string;
}

const VARIANTS: Record<'ecommerce' | 'bi', Variant> = {
  ecommerce: {
    quiz: ECOMMERCE_QUIZ,
    name: 'Ecommerce analytics setup quiz',
    seoTitle: 'Which ecommerce analytics setup fits you? A free quiz',
    description: 'Eight questions about your channels, revenue, team, and budget. Get a recommended path, from Shopify reports to Triple Whale, Peel, Sellerboard, Polar, or a warehouse build, with the reasons.',
    eyebrow: 'Free tool · Ecommerce analytics',
    heading: 'Which ecommerce analytics setup fits you?',
    intro: 'Eight questions about your channels, team, and budget. Get a recommended path, the reasons behind it, and the comparisons to read next.',
    extraLinks: [
      { href: '/blog/ecommerce-data-stack-tools-mapped', label: 'The ecommerce data stack, mapped by job' },
      { href: '/resources/ecommerce-reporting', label: 'Ecommerce reporting guides' },
    ],
    scoringNote: 'Greyfield Data builds custom ecommerce warehouses. To keep that from tilting the result, ties go to the simpler, cheaper option, and a custom build only wins when several answers point to it.',
  },
  bi: {
    quiz: BI_QUIZ,
    name: 'BI tool quiz',
    seoTitle: 'Which BI tool fits your team? Sigma, Power BI, Tableau, Looker, and more',
    description: 'Eight questions about your warehouse, report builders, Microsoft 365, write-back, embedding, governance, and budget. Get a recommendation among Sigma, Power BI, Tableau, Looker, Metabase, Hex, and Omni.',
    eyebrow: 'Free tool · BI tools',
    heading: 'Which BI tool fits your team?',
    intro: 'Eight questions about your warehouse, your report builders, and your budget. Get a recommendation among Sigma, Power BI, Tableau, Looker, Metabase, Hex, and Omni, with the reasons.',
    extraLinks: [
      { href: '/compare', label: 'All tool comparisons' },
      { href: '/blog/modern-data-stack-tools-mapped', label: 'The modern data stack, mapped by job' },
      { href: '/resources/sigma', label: 'Sigma guides' },
    ],
    scoringNote: 'Greyfield Data sells Sigma consulting. To keep that from tilting the result, Sigma is listed last when scores tie, and the full ranking for your answers is shown with the result.',
  },
};

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-5 w-5 flex-none rounded-full bg-[#e6f4ec] p-0.5 text-[#14532d]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

export default function ToolQuizPage({ variant }: { variant: 'ecommerce' | 'bi' }) {
  const config = VARIANTS[variant];
  const { quiz } = config;
  const total = quiz.questions.length;
  const [answers, setAnswers] = React.useState<Answers>({});
  const [step, setStep] = React.useState(0);
  const [edited, setEdited] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const focusTarget = React.useRef<HTMLHeadingElement | HTMLLegendElement | null>(null);
  const moved = React.useRef(false);
  const outcome = score(quiz, answers);
  const showResult = step >= total && outcome.result !== null;
  useTrackWhen(showResult, 'quiz_complete', { quiz: variant === 'ecommerce' ? 'ecommerce_analytics_tool_quiz' : 'bi_tool_quiz', result: outcome.result?.id });
  const question = quiz.questions[Math.min(step, total - 1)];

  // Read a shared result from the URL once, after hydration.
  React.useEffect(() => {
    const fromUrl = answersFromSearch(quiz, window.location.search);
    const count = Object.keys(fromUrl).length;
    if (count === 0) return;
    setAnswers(fromUrl);
    const firstMissing = quiz.questions.findIndex((q) => !(q.id in fromUrl));
    setStep(firstMissing === -1 ? total : firstMissing);
  }, [quiz, total]);

  // Only touch the address bar after the visitor answers something, keeping UTMs, the hash, and router state.
  React.useEffect(() => {
    if (!edited) return;
    const search = searchWithAnswers(quiz, answers, window.location.search);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, [answers, edited, quiz]);

  // Move focus to the new question or the result after a step change the visitor made.
  React.useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    focusTarget.current?.focus();
  }, [step]);

  function go(next: number) {
    moved.current = true;
    setCopied(false);
    setStep(Math.max(0, Math.min(total, next)));
  }

  function choose(value: string) {
    setEdited(true);
    setCopied(false);
    setAnswers((current) => ({ ...current, [question.id]: value }));
  }

  function next(event?: React.FormEvent) {
    event?.preventDefault();
    if (!(question.id in answers)) return;
    go(step + 1);
  }

  function restart() {
    setEdited(true);
    setAnswers({});
    go(0);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}${quiz.path}${searchWithAnswers(quiz, answers)}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const answeredCurrent = question.id in answers;
  const progress = showResult ? total : step + 1;
  const result = outcome.result;
  const compareLinks = (result?.compare ?? [])
    .map((slug) => ({ slug, page: getComparison(slug) }))
    .filter((entry) => entry.page);

  return (
    <BlogLayout wide>
      <SEO title={config.seoTitle} description={config.description} url={`${SITE_URL}${quiz.path}`} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: config.name, url: `${SITE_URL}${quiz.path}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">{config.name}</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#1a4e94]">{config.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight text-[#0a1628] sm:text-5xl">{config.heading}</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">{config.intro}</p>
        <p className="mt-3 text-sm text-slate-500">Your answers stay in your browser. Nothing is sent anywhere.</p>
      </header>

      <div className="mt-10 max-w-3xl">
        <div className="mb-3 flex items-baseline justify-between gap-4 text-sm text-slate-600">
          <span className="font-semibold text-[#0a1628]">{showResult ? 'Your result' : `Question ${step + 1} of ${total}`}</span>
          <span className="truncate">{showResult ? `${total} of ${total} answered` : question.label}</span>
        </div>
        <div
          role="progressbar"
          aria-label="Quiz progress"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={progress}
          aria-valuetext={showResult ? 'All questions answered' : `Question ${step + 1} of ${total}`}
          className="grid gap-1.5"
          style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        >
          {quiz.questions.map((q, index) => (
            <div key={q.id} className={`h-1.5 rounded-full ${index < progress ? 'bg-[#1a4e94]' : 'bg-slate-200'}`} />
          ))}
        </div>

        {!showResult && (
          <form onSubmit={next} className="mt-6 rounded-3xl border border-slate-200 p-5 sm:p-8">
            <fieldset key={question.id}>
              <legend
                ref={(node) => { focusTarget.current = node; }}
                tabIndex={-1}
                className="scroll-mt-32 font-display text-2xl font-bold leading-8 text-[#0a1628] outline-none"
              >
                {question.text}
              </legend>
              <div className="mt-5 space-y-3">
                {question.options.map((option) => {
                  const id = `${quiz.id}-${question.id}-${option.value}`;
                  const checked = answers[question.id] === option.value;
                  return (
                    <label
                      key={option.value}
                      htmlFor={id}
                      className={`flex min-h-[3.25rem] cursor-pointer items-start gap-3.5 rounded-2xl border px-4 py-3.5 transition focus-within:ring-2 focus-within:ring-[#1a4e94] focus-within:ring-offset-2 sm:px-5 ${checked ? 'border-[#1a4e94] bg-[#eaf2fd] shadow-[inset_0_0_0_1px_#1a4e94]' : 'border-slate-300 bg-white hover:border-[#1a4e94]'}`}
                    >
                      <input
                        id={id}
                        type="radio"
                        name={`${quiz.id}-${question.id}`}
                        value={option.value}
                        checked={checked}
                        onChange={() => choose(option.value)}
                        className="mt-1 h-[18px] w-[18px] flex-none accent-[#1a4e94]"
                        aria-describedby={option.hint ? `${id}-hint` : undefined}
                      />
                      <span className="min-w-0">
                        <span className={`block font-semibold ${checked ? 'text-[#0a1628]' : 'text-slate-900'}`}>{option.label}</span>
                        {option.hint && <span id={`${id}-hint`} className={`mt-0.5 block text-sm ${checked ? 'text-slate-700' : 'text-slate-500'}`}>{option.hint}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div className="mt-6 flex items-center justify-between gap-3">
              {step > 0 ? (
                <button type="button" onClick={() => go(step - 1)} className="min-h-[48px] rounded-xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-[#1a4e94]">Back</button>
              ) : <span />}
              <button
                type="submit"
                disabled={!answeredCurrent}
                className="min-h-[48px] rounded-xl bg-[#1a4e94] px-6 font-semibold text-white hover:bg-[#153f78] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
              >
                {step === total - 1 ? 'See my result' : 'Next question'}
              </button>
            </div>
            {!answeredCurrent && <p className="mt-3 text-right text-xs text-slate-500">Pick an answer to continue.</p>}
          </form>
        )}

        {showResult && result && (
          <section aria-labelledby="quiz-result" className="mt-6 overflow-hidden rounded-3xl border border-slate-200">
            <div className="bg-[#0a1628] p-6 text-white sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#93b4e6]">{variant === 'bi' ? 'Best fit for your answers' : 'Your recommended path'}</p>
              <h2 id="quiz-result" ref={(node) => { focusTarget.current = node; }} tabIndex={-1} className="mt-3 scroll-mt-32 font-display text-3xl font-bold leading-tight outline-none">{result.name}</h2>
              <p className="mt-3 leading-7 text-slate-300">{result.summary}</p>
            </div>
            <div className="space-y-7 p-6 sm:p-8">
              <div>
                <h3 className="font-semibold text-[#0a1628]">Why this fits your answers</h3>
                <ul className="mt-3 space-y-2.5">
                  {result.reasons.map((reason) => (
                    <li key={reason} className="flex gap-2.5 leading-7 text-slate-700"><CheckIcon /><span>{reason}</span></li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600 sm:p-5">
                <p><strong className="text-[#0a1628]">Close second: {result.runnerUp.name}.</strong> {result.runnerUp.summary}</p>
                <p className="mt-2">{result.revisit}</p>
              </div>
              <div>
                <h3 className="font-semibold text-[#0a1628]">Read next</h3>
                <ul className="mt-3 space-y-2">
                  {compareLinks.map(({ slug, page }) => (
                    <li key={slug}><Link to={`/compare/${slug}`} className="font-semibold text-[#1a4e94] underline underline-offset-4 hover:text-[#0a1628]">{page?.title}</Link></li>
                  ))}
                  {config.extraLinks.map((link) => (
                    <li key={link.href}><Link to={link.href} className="font-semibold text-[#1a4e94] underline underline-offset-4 hover:text-[#0a1628]">{link.label}</Link></li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 pt-6">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[48px] items-center rounded-xl bg-[#1a4e94] px-6 font-semibold text-white hover:bg-[#153f78]">Book a free call</a>
                <button type="button" onClick={copyLink} className="min-h-[48px] rounded-xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-[#1a4e94]">{copied ? 'Link copied' : 'Copy a link to this result'}</button>
                <button type="button" onClick={() => go(0)} className="min-h-[48px] px-2 font-semibold text-[#1a4e94] underline underline-offset-4">Change answers</button>
                <button type="button" onClick={restart} className="min-h-[48px] px-2 font-semibold text-slate-600 underline underline-offset-4">Start over</button>
              </div>
              <p role="status" className="sr-only">{copied ? 'Link copied to the clipboard.' : ''}</p>
              {result.disclosure && <p className="text-sm leading-6 text-slate-500">{result.disclosure}</p>}
            </div>
          </section>
        )}
      </div>

      {showResult && (
        <section aria-labelledby="ranking-heading" className="mt-10 max-w-3xl">
          <h2 id="ranking-heading" className="font-display text-xl font-bold text-[#0a1628]">How your answers scored</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">Each answer adds or removes points for each option. Higher is a better fit.</p>
          <ol className="mt-4 divide-y divide-slate-200 border-y border-slate-200 text-sm">
            {outcome.ranking.map((entry, index) => (
              <li key={entry.id} className="flex justify-between gap-4 py-2.5">
                <span className={index === 0 ? 'font-semibold text-[#0a1628]' : 'text-slate-700'}>{index + 1}. {entry.name}</span>
                <span className="tabular-nums text-slate-600">{entry.points} {Math.abs(entry.points) === 1 ? 'point' : 'points'}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="mt-14 grid gap-10 border-t border-slate-200 pt-12 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0a1628]">How the scoring works</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li>Every answer gives points to the options it suits and takes points from the ones it rules out.</li>
            <li>The option with the most points is the recommendation. The reasons shown are the answers that counted most toward it.</li>
            <li>{config.scoringNote}</li>
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0a1628]">Use it as a starting point</h2>
          <p className="mt-5 leading-7 text-slate-600">A quiz cannot see your data, contracts, or team. Read the comparison for your result, check current pricing with the vendor, and try it on your own data before you commit.</p>
          <div className="mt-6 flex flex-wrap gap-4">
            {config.extraLinks.map((link) => (
              <Link key={link.href} to={link.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{link.label}</Link>
            ))}
            <Link to="/resources" className="font-semibold text-[#1a4e94] underline underline-offset-4">More free tools</Link>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
}
